import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode } from 'swiper/modules';
import { ChevronLeft, ChevronRight, Sprout } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { cachedGet, BACKEND_URL } from '../lib/apiCache';

import 'swiper/css';
import 'swiper/css/free-mode';

const CategoryMenuCarousel = () => {
  const { language } = useLanguage();
  const swiperRef = useRef(null);
  const [items, setItems] = useState([]);
  const [categoriesById, setCategoriesById] = useState({});

  useEffect(() => {
    let mounted = true;

    Promise.all([
      cachedGet('/api/settings').catch(() => null),
      cachedGet('/api/categories').catch(() => []),
    ]).then(([settingsData, catsData]) => {
      if (!mounted) return;

      const list = Array.isArray(settingsData?.categoryMenuItems)
        ? settingsData.categoryMenuItems
        : [];

      const cats = catsData || [];
      const byId = {};

      cats.forEach((c) => {
        byId[c.id] = c;
      });

      setCategoriesById(byId);

      const finalItems =
        list.length > 0
          ? list.map((item) => {
              const cat = item.categoryId ? byId[item.categoryId] : null;

              return {
                ...item,
                icon:
                  item.icon ||
                  item.image ||
                  item.imageUrl ||
                  item.photo ||
                  item.thumbnail ||
                  cat?.icon ||
                  cat?.image ||
                  cat?.imageUrl ||
                  cat?.photo ||
                  cat?.thumbnail ||
                  '',
              };
            })
          : cats.map((c) => ({
              id: c.id,
              name: c.name,
              nameRu: c.nameRu,
              icon:
                c.icon ||
                c.image ||
                c.imageUrl ||
                c.photo ||
                c.thumbnail ||
                '',
              url: `/category/${c.slug || c.id}`,
              type: 'category',
              categoryId: c.id,
            }));

      setItems(finalItems);
    });

    return () => {
      mounted = false;
    };
  }, []);

  if (!items.length) return null;

  const resolveLink = (item) => {
    let url;

    if (item.url) {
      url = item.url;
    } else if (item.categoryId) {
      const cat = categoriesById[item.categoryId];
      url = cat ? `/category/${cat.slug || cat.id}` : '#';
    } else {
      url = '#';
    }

    if (language === 'ru' && url.startsWith('/') && !url.startsWith('/ru')) {
      return `/ru${url}`;
    }

    return url;
  };

  const getImageSrc = (src) => {
    if (!src) return '';
    if (src.startsWith('data:')) return src;
    if (/^https?:\/\//i.test(src)) return src;
    if (src.startsWith('/uploads')) return `${BACKEND_URL}${src}`;
    if (src.startsWith('/')) return src;

    return `${BACKEND_URL}/uploads/${src}`;
  };

  const renderIcon = (item) => {
    const linkedCat = item.categoryId ? categoriesById[item.categoryId] : null;

    const rawSrc =
      item.icon ||
      item.image ||
      item.imageUrl ||
      item.photo ||
      item.thumbnail ||
      linkedCat?.image ||
      linkedCat?.imageUrl ||
      linkedCat?.photo ||
      linkedCat?.thumbnail ||
      linkedCat?.icon ||
      '';

    const src = getImageSrc(rawSrc);

    const isImagePath =
      rawSrc &&
      (rawSrc.startsWith('data:') ||
        /^https?:\/\//i.test(rawSrc) ||
        rawSrc.startsWith('/') ||
        /\.(png|jpe?g|gif|svg|webp)$/i.test(rawSrc));

    if (src && isImagePath) {
      return (
        <img
          src={src}
          alt={item.name || ''}
          className="w-6 h-6 md:w-8 md:h-8 object-cover"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      );
    }

    if (rawSrc && rawSrc.length <= 4) {
      return (
        <span className="text-[13px] md:text-base leading-none">
          {rawSrc}
        </span>
      );
    }

    return <Sprout className="w-5 h-5 md:w-6 md:h-6 text-black" />;
  };

  const slidePrev = () => {
    swiperRef.current?.slidePrev(850);
  };

  const slideNext = () => {
    swiperRef.current?.slideNext(850);
  };

  return (
    <section
      data-testid="category-menu-carousel"
      className="bg-white"
    >
      <div className="w-full px-2 py-1 pt-4 md:px-4 md:py-2 md:pt-4">
        <div className="relative bg-[#f4f4f4] rounded-[18px] md:rounded-[26px] overflow-hidden">
          <button
            type="button"
            onClick={slidePrev}
            className="absolute left-0 top-0 z-20 flex h-full w-10 md:w-14 items-center justify-center bg-[#f4f4f4] text-[#2d2d2d]/55 transition hover:text-black"
            aria-label="Categorii anterioare"
          >
            <ChevronLeft className="w-5 h-5 md:w-7 md:h-7" strokeWidth={2.3} />
          </button>

          <div className="px-10 py-2 md:px-16 md:py-4">
            <Swiper
              modules={[FreeMode]}
              freeMode={{
                enabled: true,
                momentum: true,
                momentumRatio: 0.9,
                momentumVelocityRatio: 0.85,
              }}
              loop
              grabCursor
              slidesPerView="auto"
              speed={850}
              spaceBetween={18}
              breakpoints={{
                768: {
                  spaceBetween: 32,
                },
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              className="!overflow-visible"
            >
              {items.map((item) => {
                const linkedCat = item.categoryId
                  ? categoriesById[item.categoryId]
                  : null;

                const displayName =
                  language === 'ru'
                    ? item.nameRu ||
                      linkedCat?.nameRu ||
                      item.name ||
                      linkedCat?.name
                    : item.name || linkedCat?.name;

                return (
                  <SwiperSlide key={item.id} style={{ width: 'auto' }}>
                    <Link
                      to={resolveLink(item)}
                      className="flex items-center gap-1.5 md:gap-2 text-[13px] md:text-[17px] font-semibold text-[#2d2d2d] whitespace-nowrap hover:text-black transition"
                    >
                      <span className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center shrink-0">
                        {renderIcon(item)}
                      </span>

                      <span>{displayName}</span>
                    </Link>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>

          <button
            type="button"
            onClick={slideNext}
            className="absolute right-0 top-0 z-20 flex h-full w-10 md:w-14 items-center justify-center bg-[#f4f4f4] text-[#2d2d2d]/55 transition hover:text-black"
            aria-label="Categorii următoare"
          >
            <ChevronRight className="w-5 h-5 md:w-7 md:h-7" strokeWidth={2.3} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default CategoryMenuCarousel;